import { Stars01Icon, UserPlus01Icon } from 'primitives/icons'
import { useTranslations } from 'next-intl'
import { Link } from '@repo/web-ui/components/link'
import { AppRoutes } from '@repo/web-utils/app-routes'

import { EyeIcon } from '@web/components/icons/eye'
import { HeartIcon } from '@web/components/icons/heart'
import { SchoolIcon } from '@web/components/icons/school'
import { Button } from '@web/components/prod/button/button'
import { DesignerIcon, DownloadCircleIcon, DriveUploadIcon, GroupsIcon } from '../_components/icons'

import * as React from 'react'

export type CardVariant =
  | 'recently-viewed'
  | 'trending-now'
  | 'your-favorites'
  | 'designers-to-follow'
  | 'classes-for-you'
  | 'downloads-license-keys'
  | 'studio-projects'
  | 'flow-prompts'
  | 'file-transfers'
  | 'clubhouses'

export const useMyAccountCardVariant = (
  variant: CardVariant,
): {
  title: string
  description?: string
  icon: JSX.Element
  copy_1: string
  copy_2: string
  cta: JSX.Element
} => {
  const t = useTranslations('my_account.empty_block')
  return {
    'recently-viewed': {
      title: t('recently-viewed.title'),
      description: t('recently-viewed.description'),
      icon: <EyeIcon className="h-10 w-10 text-gray-100" />,
      copy_1: t('recently-viewed.copy_1'),
      copy_2: t('recently-viewed.copy_2'),
      cta: (
        <Button asChild variant="normal" className="h-9 shrink-0 px-3">
          <Link href={AppRoutes.HOME}>{t('recently-viewed.cta')}</Link>
        </Button>
      ),
    },
    'trending-now': {
      title: t('trending-now.title'),
      description: t('trending-now.description'),
      icon: <EyeIcon className="h-10 w-10 text-gray-100" />,
      copy_1: t('trending-now.copy_1'),
      copy_2: t('trending-now.copy_2'),
      cta: (
        <Button asChild variant="normal" className="h-9 shrink-0 px-3">
          <Link href={AppRoutes.HOME}>{t('trending-now.cta')}</Link>
        </Button>
      ),
    },
    'your-favorites': {
      title: t('your-favorites.title'),
      description: t('your-favorites.description'),
      icon: <HeartIcon className="h-10 w-10 text-gray-100" />,
      copy_1: t('your-favorites.copy_1'),
      copy_2: t('your-favorites.copy_2'),
      cta: (
        <Button asChild variant="normal" className="h-9 shrink-0 px-3">
          <Link href={AppRoutes.MY_ACCOUNT_FAVORITES}>{t('your-favorites.cta')}</Link>
        </Button>
      ),
    },
    'designers-to-follow': {
      title: t('designers-to-follow.title'),
      description: t('designers-to-follow.description'),
      icon: <UserPlus01Icon className="h-10 w-10 text-gray-100" />,
      copy_1: t('designers-to-follow.copy_1'),
      copy_2: t('designers-to-follow.copy_2'),
      cta: (
        <Button asChild variant="normal" className="h-9 shrink-0 px-3">
          <Link href={AppRoutes.FOLLOWING}>{t('designers-to-follow.cta')}</Link>
        </Button>
      ),
    },
    'classes-for-you': {
      title: t('classes-for-you.title'),
      description: t('classes-for-you.description'),
      icon: <SchoolIcon className="h-10 w-10 text-gray-100" />,
      copy_1: t('classes-for-you.copy_1'),
      copy_2: t('classes-for-you.copy_2'),
      cta: (
        <Button asChild variant="normal" className="h-9 shrink-0 px-3">
          <Link href={AppRoutes.MY_ACCOUNT_CLASSES}>{t('classes-for-you.cta')}</Link>
        </Button>
      ),
    },
    'downloads-license-keys': {
      title: t('downloads-license-keys.title'),
      description: t('downloads-license-keys.description'),
      icon: <DownloadCircleIcon className="h-10 w-10 text-gray-100" />,
      copy_1: t('downloads-license-keys.copy_1'),
      copy_2: t('downloads-license-keys.copy_2'),
      cta: (
        <Button asChild variant="normal" className="h-9 shrink-0 px-3">
          <Link href={AppRoutes.MY_ACCOUNT_LICENSES}>{t('downloads-license-keys.cta')}</Link>
        </Button>
      ),
    },
    'studio-projects': {
      title: t('studio-projects.title'),
      description: t('studio-projects.description'),
      icon: <DesignerIcon className="h-10 w-10 text-gray-100" />,
      copy_1: t('studio-projects.copy_1'),
      copy_2: t('studio-projects.copy_2'),
      cta: (
        <Button asChild variant="normal" className="h-9 shrink-0 px-3">
          <Link href={AppRoutes.STUDIO_TOOL_PROJECTS}>{t('studio-projects.cta')}</Link>
        </Button>
      ),
    },
    'flow-prompts': {
      title: t('flow-prompts.title'),
      description: t('flow-prompts.description'),
      icon: <Stars01Icon className="h-10 w-10 text-gray-100" />,
      copy_1: t('flow-prompts.copy_1'),
      copy_2: t('flow-prompts.copy_2'),
      cta: (
        <Button asChild variant="normal" className="h-9 shrink-0 px-3">
          <Link href={AppRoutes.STUDIO_TOOL_FLOW}>{t('flow-prompts.cta')}</Link>
        </Button>
      ),
    },
    'file-transfers': {
      title: t('file-transfers.title'),
      icon: <DriveUploadIcon className="h-10 w-10 text-gray-100" />,
      copy_1: t('file-transfers.copy_1'),
      copy_2: t('file-transfers.copy_2'),
      cta: (
        <Button asChild variant="normal" className="h-9 shrink-0 px-3">
          <Link href={AppRoutes.STUDIO_TOOL_TRANSFER}>{t('file-transfers.cta')}</Link>
        </Button>
      ),
    },
    clubhouses: {
      title: t('clubhouses.title'),
      description: t('clubhouses.description'),
      icon: <GroupsIcon className="h-10 w-10 text-gray-100" />,
      copy_1: t('clubhouses.copy_1'),
      copy_2: t('clubhouses.copy_2'),
      cta: (
        <Button asChild variant="normal" className="h-9 shrink-0 px-3">
          <Link href={AppRoutes.CLUBHOUSE}>{t('clubhouses.cta')}</Link>
        </Button>
      ),
    },
  }[variant]
}
